export const NATIVE_HLS_MIME_TYPES = [
  "application/vnd.apple.mpegurl",
  "application/x-mpegURL",
] as const;

export type NativeHlsCapabilityEnvironment = {
  userAgent?: string;
  hasManagedMediaSource?: boolean;
};

const NON_SAFARI_WEBKIT_TOKENS = /\b(?:Chrome|Chromium|CriOS|FxiOS|Firefox|Edg|OPR|Opera|SamsungBrowser)\b/i;

export function canPlayNativeHlsResult(result: string): boolean {
  return result.trim() !== "";
}

export function isSafariNativeHlsUserAgent(userAgent: string): boolean {
  const normalizedUserAgent = userAgent.trim();
  if (!normalizedUserAgent) {
    return false;
  }
  if (!/AppleWebKit/i.test(normalizedUserAgent) || !/Safari/i.test(normalizedUserAgent)) {
    return false;
  }
  return !NON_SAFARI_WEBKIT_TOKENS.test(normalizedUserAgent);
}

export function environmentSupportsNativeHls(environment: NativeHlsCapabilityEnvironment = {}): boolean {
  const userAgent = environment.userAgent ?? (
    typeof navigator === "undefined" ? "" : navigator.userAgent
  );
  const hasManagedMediaSource = environment.hasManagedMediaSource ?? (
    typeof globalThis !== "undefined" && "ManagedMediaSource" in globalThis
  );
  return hasManagedMediaSource || isSafariNativeHlsUserAgent(userAgent);
}

export function supportsNativeHls(
  canPlayType: (mimeType: string) => string,
  environment?: NativeHlsCapabilityEnvironment,
): boolean {
  if (!environmentSupportsNativeHls(environment)) {
    return false;
  }
  return NATIVE_HLS_MIME_TYPES.some((mimeType) => {
    try {
      return canPlayNativeHlsResult(canPlayType(mimeType));
    } catch {
      return false;
    }
  });
}

export function nativeHlsResultsSupported(
  canPlayTypeResults: string[],
  environment?: NativeHlsCapabilityEnvironment,
): boolean {
  return environmentSupportsNativeHls(environment) && canPlayTypeResults.some(canPlayNativeHlsResult);
}

export function videoSupportsNativeHls(
  video: HTMLVideoElement,
  environment?: NativeHlsCapabilityEnvironment,
): boolean {
  return supportsNativeHls((mimeType) => video.canPlayType(mimeType), environment);
}

export function browserSupportsNativeHls(): boolean {
  if (typeof document === "undefined") {
    return false;
  }
  return videoSupportsNativeHls(document.createElement("video"));
}
